import React from 'react';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function statusLabel(m) {
  if (m?.membershipStatus === 'FROZEN') return 'Donduruldu';
  if (m?.membershipStatus === 'PASSIVE') return 'Pasif';
  if (m?.membershipStatus === 'ACTIVE') return 'Aktif';
  if (typeof m?.active === 'boolean') return m.active ? 'Aktif' : 'Pasif';
  return '—';
}

function usageLabel(m) {
  const used = Number(m?.usedSessions ?? 0);
  const total = Number(m?.totalSessions ?? 0);
  if (!Number.isFinite(total) || total <= 0) return null;
  const u = Number.isFinite(used) ? Math.max(0, used) : 0;
  const t = Math.max(0, total);
  return `${u}/${t} kullanım`;
}

function usageTone(m) {
  const used = Number(m?.usedSessions ?? 0);
  const total = Number(m?.totalSessions ?? 0);
  if (!(total > 0)) return 'neutral';
  if (used >= total) return 'error';
  if (used / total >= 0.75) return 'warning';
  return 'success';
}

function debtTry(m) {
  if (typeof m?.debtTry === 'number') return Math.max(0, m.debtTry);
  if (typeof m?.balanceCents === 'number') return Math.max(0, Math.round(m.balanceCents / 100));
  if (typeof m?.balanceTry === 'number') return Math.max(0, m.balanceTry);
  return 0;
}

function dateShort(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  return `${dd}.${mm}.${yy}`;
}

export function MembersAccordionItem({ member, expanded, onToggle, onDetail, onOpenMenu }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!member) return null;

  const name =
    member?.fullName ||
    `${member?.name || ''} ${member?.surname || ''}`.trim() ||
    `#${member?.id ?? ''}`;

  const phone = member?.phone || '—';
  const planName = member?.planName || member?.activePlanName || '—';
  const debt = debtTry(member);
  const st = statusLabel(member);

  const usage = usageLabel(member);
  const usageT = usageTone(member);

  const pillSx = {
    borderRadius: '999px',
    fontWeight: 800,
    background: theme.palette.background.paper,
  };

  const chipCommonSx = {
    ...pillSx,
    height: isMobile ? 28 : 30,
    '& .MuiChip-label': { px: isMobile ? 0.9 : 1.1, fontSize: isMobile ? 12 : 12.5 },
    '& .MuiChip-icon': { ml: 0.8, mr: -0.2 },
  };

  const actionBtnSx = {
    width: isMobile ? 32 : 38,
    height: isMobile ? 32 : 38,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  };

  return (
    <Card
      sx={{
        borderRadius: isMobile ? '14px' : '16px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
        background: theme.palette.background.paper,
      }}
    >
      {/* header row */}
      <MDBox
        sx={{
          p: isMobile ? 1.2 : 1.5,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr auto',
          alignItems: 'start',
          gap: 1,
        }}
      >
        {/* left */}
        <MDBox onClick={() => onDetail?.(member)} sx={{ cursor: 'pointer', minWidth: 0 }}>
          {/* name + status */}
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDTypography
              variant="button"
              fontWeight="bold"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: isMobile ? 14 : 13.5,
              }}
            >
              {name}
            </MDTypography>

            <Chip
              size="small"
              variant="outlined"
              label={st}
              sx={{
                ...chipCommonSx,
                borderColor: st === 'Aktif' ? 'rgba(76,175,80,.35)' : 'rgba(244,67,54,.30)',
                background: st === 'Aktif' ? 'rgba(76,175,80,.06)' : 'rgba(244,67,54,.05)',
              }}
            />
          </MDBox>

          {/* subtitle */}
          <MDTypography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              mt: 0.2,
            }}
          >
            {phone} • {isMobile ? `P: ${planName}` : `Paket: ${planName}`}
          </MDTypography>

          {/* pills row: mobile = horizontal scroll */}
          <MDBox
            sx={{
              mt: 0.9,
              display: 'flex',
              gap: 0.8,
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              overflowX: isMobile ? 'auto' : 'visible',
              WebkitOverflowScrolling: 'touch',
              pr: isMobile ? 0.5 : 0,
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Chip
              size="small"
              variant="outlined"
              icon={
                <Icon sx={{ fontSize: 16 }}>{debt > 0 ? 'priority_high' : 'check_circle'}</Icon>
              }
              label={debt > 0 ? `Borç: ${debt} ₺` : 'Borç: 0 ₺'}
              sx={{
                ...chipCommonSx,
                borderColor: debt > 0 ? 'rgba(244,67,54,.35)' : 'rgba(76,175,80,.35)',
                background: debt > 0 ? 'rgba(244,67,54,.06)' : 'rgba(76,175,80,.06)',
              }}
            />

            {usage ? (
              <Chip
                size="small"
                variant="outlined"
                icon={<Icon sx={{ fontSize: 16 }}>fitness_center</Icon>}
                label={usage}
                sx={{
                  ...chipCommonSx,
                  borderColor:
                    usageT === 'error'
                      ? 'rgba(244,67,54,.35)'
                      : usageT === 'warning'
                      ? 'rgba(255,152,0,.40)'
                      : 'rgba(76,175,80,.35)',
                  background:
                    usageT === 'error'
                      ? 'rgba(244,67,54,.06)'
                      : usageT === 'warning'
                      ? 'rgba(255,152,0,.08)'
                      : 'rgba(76,175,80,.06)',
                }}
              />
            ) : null}

            <Chip
              size="small"
              variant="outlined"
              icon={<Icon sx={{ fontSize: 16 }}>event</Icon>}
              label={`Kayıt: ${dateShort(member?.createdAt)}`}
              sx={chipCommonSx}
            />
          </MDBox>
        </MDBox>

        {/* right buttons */}
        <MDBox
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            justifyContent: isMobile ? 'flex-end' : 'flex-start',
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDetail?.(member);
            }}
            sx={actionBtnSx}
          >
            <Icon sx={{ fontSize: isMobile ? 18 : 20 }}>chevron_right</Icon>
          </IconButton>

          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onOpenMenu?.(e, member);
            }}
            sx={actionBtnSx}
          >
            <Icon sx={{ fontSize: isMobile ? 18 : 20 }}>more_vert</Icon>
          </IconButton>
        </MDBox>
      </MDBox>

      <Collapse in={!!expanded} timeout="auto" unmountOnExit>
        <Divider />
        <MDBox sx={{ p: 1.5 }}>
          <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            Detay görünümü (mobile) — buraya “kalan seans / hediye / dondurma” gibi alanlar
            ekleyebiliriz.
          </MDTypography>
        </MDBox>
      </Collapse>
    </Card>
  );
}
